/**
 * Componente de configuração 2FA para a página de Perfil
 * Steps: 1) QR Code + secret → 2) Verificar código → 3) Backup codes
 */

import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';
import { T } from '../styles/tokens';
import { Button, Spinner } from '../components/ui';
import { showToast } from '../components/Toast';

interface TwoFactorSetupProps {
  onComplete?: () => void;
}

export function TwoFactorSetup({ onComplete }: TwoFactorSetupProps) {
  const [status, setStatus] = useState<'loading' | 'off' | 'setup' | 'verify' | 'done' | 'enabled'>('loading');
  const [secret, setSecret] = useState('');
  const [qrUri, setQrUri] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [code, setCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [remainingCodes, setRemainingCodes] = useState(0);

  useEffect(() => { loadStatus(); }, []);

  async function loadStatus() {
    try {
      const res = await api.get<{ enabled: boolean; remainingBackupCodes: number }>('/auth/2fa/status');
      setRemainingCodes(res.remainingBackupCodes);
      setStatus(res.enabled ? 'enabled' : 'off');
    } catch { setStatus('off'); }
  }

  async function startSetup() {
    setSaving(true);
    try {
      const res = await api.post<{ secret: string; qrCodeUri: string; backupCodes: string[] }>('/auth/2fa/setup');
      setSecret(res.secret);
      setQrUri(res.qrCodeUri);
      setBackupCodes(res.backupCodes);
      setStatus('setup');
    } catch (e: any) {
      showToast(e.message || 'Erro ao iniciar setup', 'error');
    }
    setSaving(false);
  }

  async function verifySetup() {
    if (code.length !== 6) return;
    setSaving(true);
    try {
      await api.post('/auth/2fa/verify-setup', { token: code });
      setStatus('done');
      showToast('2FA ativado com sucesso! ✅', 'success');
    } catch (e: any) {
      showToast(e.message || 'Código inválido', 'error');
    }
    setSaving(false);
  }

  async function disable2FA() {
    if (disableCode.length !== 6) return;
    setSaving(true);
    try {
      await api.post('/auth/2fa/disable', { token: disableCode });
      setStatus('off');
      setDisableCode('');
      showToast('2FA desativado', 'success');
      onComplete?.();
    } catch (e: any) {
      showToast(e.message || 'Código inválido', 'error');
    }
    setSaving(false);
  }

  const cardStyle: any = {
    background: T.bgInput, borderRadius: 12, border: '1px solid rgba(255,255,255,0.08)',
    padding: 20, marginBottom: 16,
  };

  if (status === 'loading') return <div style={cardStyle}><Spinner size={20} /></div>;

  // ── 2FA Ativado ──
  if (status === 'enabled') {
    return (
      <div style={cardStyle}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          🔐 Autenticação em Dois Fatores (2FA)
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <span style={{
            padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
            background: 'rgba(16,185,129,0.12)', color: '#10B981',
          }}>2FA Ativo ✅</span>
          <span style={{ fontSize: 12, color: T.textSecondary }}>
            {remainingCodes} códigos de backup restantes
          </span>
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={{ display: 'block', fontSize: 12, color: T.textSecondary, marginBottom: 6, fontWeight: 600 }}>
            Para desativar, insira o código do seu app autenticador:
          </label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              maxLength={6}
              value={disableCode}
              onInput={(e) => setDisableCode((e.target as HTMLInputElement).value.replace(/\D/g, ''))}
              placeholder="000000"
              style={{
                width: 120, padding: '10px 14px', borderRadius: 8, fontSize: 18, textAlign: 'center' as any,
                letterSpacing: 8, fontFamily: 'monospace', fontWeight: 700,
                background: T.bg, border: '1px solid #374151', color: T.text, outline: 'none',
              }}
            />
            <Button variant="danger" onClick={disable2FA} disabled={saving || disableCode.length !== 6}>
              {saving ? 'Desativando...' : 'Desativar 2FA'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Setup Step 1: QR Code ──
  if (status === 'setup') {
    return (
      <div style={cardStyle}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          🔐 Configurar 2FA — Passo 1 de 2
        </h2>
        <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 16 }}>
          Escaneie o QR code com seu app autenticador (Google Authenticator, Authy, etc.)
        </p>

        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' as any, marginBottom: 20 }}>
          <div style={{ background: '#fff', padding: 8, borderRadius: 8 }}>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(qrUri)}&size=200x200`}
              alt="QR Code 2FA" width={200} height={200}
            />
          </div>
          <div>
            <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 8 }}>
              Ou copie o código manualmente:
            </p>
            <div style={{
              fontFamily: 'monospace', fontSize: 13, color: T.primary, padding: '10px 14px',
              background: 'rgba(47,107,255,0.08)', borderRadius: 8, wordBreak: 'break-all' as any,
              border: '1px solid rgba(47,107,255,0.2)', maxWidth: 280, cursor: 'pointer',
            }}
              onClick={() => { navigator.clipboard.writeText(secret); showToast('Copiado!', 'success'); }}
              title="Clique para copiar"
            >
              {secret}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, color: T.textSecondary, marginBottom: 6, fontWeight: 600 }}>
            Digite o código de 6 dígitos do app:
          </label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              maxLength={6}
              value={code}
              onInput={(e) => {
                const val = (e.target as HTMLInputElement).value.replace(/\D/g, '');
                setCode(val);
                if (val.length === 6) setTimeout(() => verifySetup(), 100);
              }}
              autoFocus
              placeholder="000000"
              style={{
                width: 160, padding: '12px 16px', borderRadius: 8, fontSize: 24, textAlign: 'center' as any,
                letterSpacing: 10, fontFamily: 'monospace', fontWeight: 700,
                background: T.bg, border: '1px solid #374151', color: T.text, outline: 'none',
              }}
            />
            <Button variant="primary" onClick={verifySetup} disabled={saving || code.length !== 6}>
              {saving ? 'Verificando...' : 'Verificar'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Setup Done: Mostrar backup codes ──
  if (status === 'done') {
    return (
      <div style={cardStyle}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          ✅ 2FA Ativado com Sucesso!
        </h2>
        <div style={{
          padding: '12px 16px', borderRadius: 8, marginBottom: 16,
          background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
        }}>
          <p style={{ fontSize: 13, color: '#F59E0B', fontWeight: 700, margin: '0 0 8px' }}>
            ⚠️ Salve estes códigos de backup em local seguro!
          </p>
          <p style={{ fontSize: 12, color: T.textSecondary, margin: 0 }}>
            Cada código pode ser usado apenas uma vez caso você perca acesso ao app autenticador.
          </p>
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8,
          padding: 16, background: T.bg, borderRadius: 8, border: '1px solid #374151',
          marginBottom: 16,
        }}>
          {backupCodes.map((c, i) => (
            <div key={i} style={{
              fontFamily: 'monospace', fontSize: 14, fontWeight: 700,
              color: T.text, padding: '6px 12px', background: 'rgba(255,255,255,0.04)',
              borderRadius: 6, textAlign: 'center' as any,
            }}>
              {c}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="primary" onClick={() => {
            navigator.clipboard.writeText(backupCodes.join('\n'));
            showToast('Códigos copiados! ✅', 'success');
          }}>
            📋 Copiar Códigos
          </Button>
          <Button variant="ghost" onClick={() => { setStatus('enabled'); setRemainingCodes(backupCodes.length); onComplete?.(); }}>
            Concluído
          </Button>
        </div>
      </div>
    );
  }

  // ── 2FA Desativado ──
  return (
    <div style={cardStyle}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        🔐 Autenticação em Dois Fatores (2FA)
      </h2>
      <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 16 }}>
        Adicione uma camada extra de segurança à sua conta. Ao ativar, será necessário um código do app autenticador além do login normal.
      </p>
      <Button variant="primary" onClick={startSetup} disabled={saving}>
        {saving ? 'Carregando...' : '🔐 Ativar 2FA'}
      </Button>
    </div>
  );
}
